// =====================================================
// 白板系统 - KeepAlive 编辑器容器
// 同时挂载所有已打开的编辑器实例，非激活的用 display:none 隐藏
// 切换标签时不卸载组件，保留 Excalidraw 画布状态
// =====================================================

import React, { useEffect, useState } from 'react';
import { WhiteboardEditorPage } from '../pages/WhiteboardEditorPage';
import { useWhiteboardTabsStore } from '../stores/whiteboard-tabs-store';

/**
 * KeepAlive 编辑器容器
 *
 * 渲染所有打开的标签页对应的编辑器实例。
 * 仅当前激活的标签页可见 (display: flex)，
 * 其余标签页隐藏 (display: none) 但保留在 DOM 中，
 * 从而保留 Excalidraw 画布、协作连接和所有内存状态。
 */
export function KeepAliveEditorContainer() {
  const tabs = useWhiteboardTabsStore((s) => s.tabs);
  const activeTabId = useWhiteboardTabsStore((s) => s.activeTabId);

  // 仅在标签首次激活时才挂载（避免启动时同时加载所有恢复的标签），挂载后常驻
  const [mountedIds, setMountedIds] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    setMountedIds((prev) => {
      const liveIds = new Set(tabs.map((t) => t.id));
      const next = new Set([...prev].filter((id) => liveIds.has(id)));
      if (activeTabId && liveIds.has(activeTabId)) next.add(activeTabId);
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) return prev;
      return next;
    });
  }, [tabs, activeTabId]);

  if (tabs.length === 0) return null;

  return (
    <>
      {tabs.filter((tab) => mountedIds.has(tab.id)).map((tab) => {
        const isActive = tab.id === activeTabId;
        return (
          <div
            key={tab.id}
            style={{
              display: isActive ? 'flex' : 'none',
              flex: 1,
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <WhiteboardEditorKeepAlive id={tab.id} isActive={isActive} />
          </div>
        );
      })}
    </>
  );
}

/**
 * 编辑器包装器 — 为每个标签页提供独立的 id 和 isActive 属性
 */
const WhiteboardEditorKeepAlive = React.memo(function WhiteboardEditorKeepAlive({
  id,
  isActive,
}: {
  id: string;
  isActive: boolean;
}) {
  return <WhiteboardEditorPage keepAliveId={id} isActive={isActive} />;
});
